import { FetchResult, InternalRefetchQueriesInclude } from '@apollo/client';
import { AUTO_POPULATED_PLACES_COUNT } from '@apps/admin/routes/WebsiteEditor/routes/WebsiteEditorCustomPageV2/components/CustomPageLeftPanel/components/CustomComponents/TravelMap/components/VenueInfo/VenueInfo.constants';
import {
  AutoPopulateAccommodationPlacesMutation,
  AutoPopulateAccommodationPlacesPayload,
  AutoPopulateAccommodationPlacesV2Mutation,
  useAutoPopulateAccommodationPlacesMutation,
  useAutoPopulateAccommodationPlacesV2Mutation
} from '@graphql/generated';
import { AnalyticsContext } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addAction, addError } from '@shared/utils/logger';
import { useContext } from 'react';

interface AutoPopulateAccommodationPlacesArgs {
  onCompleted?: () => void;
  onError?: () => void;
  refetchQueries?: ((result: FetchResult) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude;
}

export const useAutoPopulateAccommodationPlaces = ({ onCompleted, onError, refetchQueries }: AutoPopulateAccommodationPlacesArgs) => {
  const analytics = useContext(AnalyticsContext);
  const [accommodationsAutoPopulatePlacesV2Experiment] = useIsFeatureEnabled('accommodationsAutoPopulatePlacesV2Experiment');

  const [autoPopulateAccommodationPlaces, { loading }] = useAutoPopulateAccommodationPlacesMutation({ onCompleted, onError });
  const [autoPopulateAccommodationPlacesV2, { loading: loadingV2 }] = useAutoPopulateAccommodationPlacesV2Mutation({ onCompleted, onError });

  const callMutation = accommodationsAutoPopulatePlacesV2Experiment ? autoPopulateAccommodationPlacesV2 : autoPopulateAccommodationPlaces;

  const convertToAutoPopulatedPlaceIds = useEventCallback((data: AutoPopulateAccommodationPlacesV2Mutation | AutoPopulateAccommodationPlacesMutation) => {
    let places: string[] = [];
    if ('autoPopulateAccommodationPlacesV2' in data) {
      places = data.autoPopulateAccommodationPlacesV2.map(place => place.id);
    } else if ('autoPopulateAccommodationPlaces' in data && data.autoPopulateAccommodationPlaces?.length) {
      places = data.autoPopulateAccommodationPlaces.map(place => place?.id).filter((id): id is string => Boolean(id));
    }
    return places;
  });

  const callAutoPopulateAccommodationPlaces = (
    payloadArgs: AutoPopulateAccommodationPlacesPayload & { locationName?: string | null; locationAddress?: string | null; locationPlaceId?: string | null; source: string }
  ): Promise<string[] | boolean> => {
    const { locationName, locationAddress, locationPlaceId, source, ...payload } = payloadArgs;
    return new Promise(resolve => {
      callMutation({ variables: { payload }, refetchQueries, awaitRefetchQueries: true })
        .then(response => {
          if (response.data) {
            const placesIds = convertToAutoPopulatedPlaceIds(response.data);
            analytics.track({
              category: 'accommodations',
              action: `TravelButtonInteracted`,
              label: 'autoPopulateAccommodationPlaces',
              extraInfo: {
                name: `autoPopulateAccommodationPlaces`,
                eventId: payload.eventId,
                latitude: payload.location?.latitude,
                longitude: payload.location?.longitude,
                placesCount: placesIds.length,
                locationPlaceId,
                locationName,
                locationAddress,
                source
              }
            });
            addAction('autoPopulateAccommodationPlaces', {
              message: 'success',
              context: 'AutoPopulate',
              eventId: payload.eventId,
              latitude: payload.location?.latitude,
              longitude: payload.location?.longitude,
              placesCount: placesIds.length,
              locationPlaceId,
              locationName,
              locationAddress,
              source
            });
            if (source === 'createWedding') {
              localStorage.setItem(AUTO_POPULATED_PLACES_COUNT(payload.eventId), JSON.stringify([placesIds.length, 0]));
            }
            resolve(placesIds);
          }
          if (response.errors) {
            addError(response.errors, {
              context: 'AutoPopulate',
              eventId: payload.eventId,
              latitude: payload.location?.latitude,
              longitude: payload.location?.longitude,
              locationPlaceId,
              locationName,
              locationAddress,
              source
            });
            resolve(true);
          }
        })
        .catch(error => {
          addError(error, {
            context: 'AutoPopulate',
            eventId: payload.eventId,
            latitude: payload.location?.latitude,
            longitude: payload.location?.longitude,
            locationPlaceId,
            locationName,
            locationAddress,
            source
          });
          resolve(true);
        });
    });
  };

  return { callAutoPopulateAccommodationPlaces, loading: loading || loadingV2 };
};
